import styled, { css } from 'styled-components';
import {
  FONT_COLOURS,
  FONT_WEIGHT,
  getBodyFont,
  getHeadingFont,
  hasAlignment,
  hasColor as hasColorStyles,
  isFocused,
  spacingMixin,
  textShimmer
} from '@belong/themes';
import { FontSizeHeadingKey, IHeading, IText, MaybeStyledCSS, StyledCSS } from '@belong/types';

export type Variants = 'large' | 'medium' | 'small';

export const getAsPropFromVariant = ({ variant }: IHeading): any => {
  switch (variant) {
    case 'xsmall':
      return 'h6';
    case 'small':
      return 'h5';
    case 'medium':
      return 'h4';
    case 'xlarge':
      return 'h2';
    case 'xxlarge':
      return 'h1';
    case 'large':
    default:
      return 'h3';
  }
};

export const Text = styled.div<IText>`
  ${hasAlignment}
  ${hasColorStyles}
`;

export interface ICopy extends IText {
  isBold?: boolean;
  variant?: Variants;
}

/**
 * Callers must omit attrs-backed props to use these defaults; explicitly passing `undefined` resets them.
 */
export const Copy = styled(Text).attrs<ICopy>(props => ({
  hasColor: props.hasColor,
  variant: props.variant || 'medium',
  as: props.as || 'p'
}))<ICopy>`
  ${({ isBold, hasColor }): MaybeStyledCSS => {
    const bold = css`
      font-weight: ${FONT_WEIGHT.SEMI_BOLD};
      color: ${hasColor === FONT_COLOURS.BASE && FONT_COLOURS.STRONG};
    `;

    return isBold
      ? bold
      : css`
          font-weight: ${FONT_WEIGHT.REGULAR};
          > strong {
            ${bold}
          }
        `;
  }}

  ${({ theme, hasColor }) =>
    !hasColor &&
    css`
      color: ${theme.foregroundColor};
      > strong {
        color: ${theme.foregroundColor};
      }
    `}

  ${({ variant }) => variant && getBodyFont(variant)}

  ${({ variant, hasColor, theme }): StyledCSS => {
    if (variant === 'small' && !hasColor) {
      return css`
        &,
        & > strong {
          color: ${theme.foregroundColorLight};
        }
      `;
    }
  }}
`;

/**
 * Callers must omit attrs-backed props to use these defaults; explicitly passing `undefined` resets them.
 */
export const Heading = styled(Text).attrs<IHeading>(props => ({
  hasColor: props.hasColor,
  variant: props.variant || 'large',
  hasVisibleFocus: props.hasVisibleFocus,
  as: props.as || getAsPropFromVariant(props)
}))<IHeading>`
  ${spacingMixin}

  ${({ colourGradient }) => (colourGradient ? textShimmer : '')};

  color: ${({ colourGradient, hasColor, theme }) =>
    colourGradient
      ? // use the theme color when present or transparent for shimmer affected
        'transparent'
      : (hasColor ?? theme.foregroundColorDark)};

  text-transform: none;

  /* Visible focus outline for programmatic focus (a11y requirement) */
  ${({ hasVisibleFocus }) =>
    hasVisibleFocus &&
    css`
      &[tabindex='-1']:focus,
      &:focus-visible {
        ${isFocused()}
      }
    `}

  ${({ variant }) => variant && getHeadingFont(variant)}

  ${({ variant }) => {
    if (!variant) {
      return;
    }

    const semiBoldVariants: FontSizeHeadingKey[] = ['xsmall', 'small'];
    const isSemiBold = semiBoldVariants.includes(variant);

    return css`
      ${getHeadingFont(variant)}
      font-weight: ${isSemiBold ? FONT_WEIGHT.SEMI_BOLD : FONT_WEIGHT.BOLD};
    `;
  }};
`;
